import { useMemo, useState, useEffect } from "react";
import TableEmpresas from '../Components/TableEmpresas';
import Api from '../Auth/Api';
import { useForm } from "react-hook-form";
import { Modal, Toast } from 'react-bootstrap';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import Select from 'react-select';
import { decodeJWT } from "../Components/jwtUtils";

function Empresas() {
    const token = JSON.parse(localStorage.getItem("user_token"));
    const nivel = decodeJWT(token).nivel;

    const LoadingScreen = () => {
        return (
            <div className="d-flex align-items-center justify-content-center p-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        )
    }

    const [showModalEditarEmpresa, setShowModalEditarEmpresa] = useState(false);
    const [showModalDeletarEmpresa, setShowModalDeletarEmpresa] = useState({ show: false, id: null, nome: '' });
    const [toast, setToast] = useState({});
    const [showToast, setShowToast] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingSave, setLoadingSave] = useState(false);

    const { register, handleSubmit, setValue, formState: { errors } } = useForm();

    const [dataEmpresas, setDataEmpresas] = useState([]);
    const [dataUsuarios, setDataUsuarios] = useState([]);
    const [dataCursos, setDataCursos] = useState([]);
    const [selectedUsuario, setSelectedUsuario] = useState(null);
    const [selectedCursos, setSelectedCursos] = useState([]);

    useEffect(() => {
        setLoading(true);
        async function fetchData() {
            try {
                const response = await Api.get(`api/listar-empresas`, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                setDataEmpresas(response.data);
                setLoading(false);
            } catch (error) {
                console.error(error.response?.data?.message || 'Erro ao carregar empresas');
                setLoading(false);
                setDataEmpresas([]);
            }
        }
        fetchData();
    }, [token, toast]);

    // Buscar usuários para seleção (apenas para administradores)
    useEffect(() => {
        if (nivel === 'Administrador') {
            async function fetchUsuarios() {
                try {
                    const response = await Api.get(`api/listar-usuarios`, {
                        headers: {
                            Authorization: `Bearer ${token}`
                        }
                    });
                    setDataUsuarios(response.data);
                } catch (error) {
                    console.error('Erro ao carregar usuários:', error);
                    setDataUsuarios([]);
                }
            }
            fetchUsuarios();
        }
    }, [token, nivel]);

    // Buscar cursos para seleção
    useEffect(() => {
        async function fetchCursos() {
            try {
                const response = await Api.get(`api/listar-todos-cursos`, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                setDataCursos(response.data);
            } catch (error) {
                console.error('Erro ao carregar cursos:', error);
                setDataCursos([]);
            }
        }
        fetchCursos();
    }, [token]);

    const onSubmitEditarEmpresa = async (data) => {
        setLoadingSave(true);
        
        // Adicionar usuario_id se selecionado (apenas para administradores)
        if (nivel === 'Administrador' && selectedUsuario) {
            data.usuario_id = selectedUsuario.value;
        }
        
        // Adicionar cursos selecionados
        if (selectedCursos && selectedCursos.length > 0) {
            data.cursos = selectedCursos;
        } else {
            data.cursos = [];
        }
        
        try {
            const response = await Api.post(`api/editar-empresa/${data.id}`, data, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            setToast({ header: 'Sucesso!', body: response.data.message });
        } catch (error) {
            setToast({ header: 'Ops!', body: error.response?.data?.message || 'Erro ao editar empresa' });
            console.error("Erro ao editar empresa:", error);
        }
        setLoadingSave(false);
        setShowToast(true);
        setShowModalEditarEmpresa(false);
        setSelectedUsuario(null);
        setSelectedCursos([]);
    }

    const handleDeletarEmpresa = async (id) => {
        try {
            const response = await Api.delete(`api/deletar-empresa/${id}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            setToast({ header: 'Sucesso!', body: response.data.message });
        } catch (error) {
            setToast({ header: 'Ops!', body: error.response?.data?.message || 'Erro ao deletar empresa' });
            console.error("Erro ao deletar empresa:", error);
        }
        setShowModalDeletarEmpresa({ show: false, id: null, nome: '' });
        setShowToast(true);
    };

    const columns = useMemo(
        () => [
            {
                Header: '',
                accessor: 'id',
                Cell: ({ value }) => (
                    <span className="fw-bold text-primary">{value}</span>
                )
            },
            {
                Header: 'Título',
                accessor: 'titulo',
                Cell: ({ value }) => (
                    <span className="fw-medium">{value}</span>
                )
            },
            {
                Header: 'Criado por',
                accessor: 'criado_por',
                Cell: ({ value }) => (
                    <span className="text-muted">{value || 'N/A'}</span>
                )
            },
            {
                Header: 'Data de Criação',
                accessor: 'created_at',
                Cell: ({ value }) => (
                    <span className="text-muted">
                        {value ? new Date(value).toLocaleDateString('pt-BR') : 'N/A'}
                    </span>
                )
            },
            {
                Header: 'Ações',
                accessor: 'acoes',
                Cell: ({ row: { original } }) => (
                    <div className="text-center">
                        <Tippy content="Editar empresa">
                            <button
                                onClick={() => {
                                    setValue('id', original.id);
                                    setValue('titulo', original.titulo);
                                    
                                    // Se for administrador, definir usuário atual como selecionado
                                    if (nivel === 'Administrador' && original.usuario_id) {
                                        const usuarioAtual = dataUsuarios.find(u => u.id === original.usuario_id);
                                        if (usuarioAtual) {
                                            setSelectedUsuario({
                                                value: usuarioAtual.id,
                                                label: usuarioAtual.nome_completo
                                            });
                                        }
                                    }
                                    
                                    // Definir cursos atuais como selecionados
                                    let cursosAtuais = [];
                                    if (original.permissao_cursos && original.permissao_cursos.trim() !== "") {
                                        try {
                                            const cursosArray = JSON.parse(original.permissao_cursos);
                                            cursosAtuais = cursosArray.map(cursoId => {
                                                const curso = dataCursos.find(c => c.id === cursoId);
                                                return curso ? {
                                                    value: curso.id,
                                                    label: curso.nome
                                                } : null;
                                            }).filter(Boolean);
                                        } catch (error) {
                                            console.error("Erro ao analisar permissao_cursos da empresa:", error);
                                        }
                                    }
                                    setSelectedCursos(cursosAtuais);
                                    
                                    setShowModalEditarEmpresa(true);
                                }}
                                className="btn btn-outline-primary justify-content-center align-items-center rounded px-2 me-2 btn_editar"
                            >
                                <i className="bi bi-pencil" />
                            </button>
                        </Tippy>

                        <Tippy content="Deletar empresa">
                            <button
                                onClick={() => setShowModalDeletarEmpresa({
                                    show: true,
                                    id: original.id,
                                    nome: original.titulo
                                })}
                                className="btn btn-outline-primary justify-content-center align-items-center rounded px-2"
                            >
                                <i className="bi bi-trash" />
                            </button>
                        </Tippy>
                    </div>
                )
            }
        ],
        [setValue, nivel, dataUsuarios, dataCursos]
    );

    // Preparar opções de usuários para o Select
    const usuariosOptions = dataUsuarios.map(usuario => ({
        value: usuario.id,
        label: usuario.nome_completo
    }));

    // Preparar opções de cursos para o Select
    const cursosOptions = dataCursos.map(curso => ({
        value: curso.id,
        label: curso.nome
    }));

    const customStyles = {
        container: (base) => ({
            ...base,
            width: '100%',
        }),
        control: (base, state) => ({
            ...base,
            fontSize: "1rem",
            backgroundColor: "#ffffff",
            borderRadius: "0.25rem",
            boxShadow: state.isFocused ? "0 0 0 0.25rem #27485b73" : null,
            borderColor: "#dee2e6",
            "&:hover": {
                borderColor: "#dee2e6"
            },
        }),
        singleValue: (base) => ({
            ...base,
            color: '#000',
            marginTop: '5px',
        }),
        input: (base) => ({
            ...base,
            color: '#adb5bd',
            height: "46px",
            margin: '0px',
            padding: '0px !important',

        }),
        menu: (base) => ({
            ...base,
            backgroundColor: '#fff',
        }),
        option: (base, state) => ({
            ...base,
            color: state.isSelected ? '#3a5a6c' : null,
            backgroundColor: state.isFocused ? '#27485b73' : null,
            "&:active": {
                backgroundColor: "#27485b73"
            },
        }),
    };

    return (
        <>
            <Toast show={showToast} onClose={() => setShowToast(false)} delay={3000} autohide style={{ position: 'fixed', left: '95%', bottom: '10px', transform: 'translateX(-95%)', zIndex: 9999, padding: 0 }}>
                <Toast.Header>
                    <strong className="mr-auto">{toast.header}</strong>
                </Toast.Header>
                <Toast.Body>{toast.body}</Toast.Body>
            </Toast>

            <div className="fade-in w-100">
                <span className="text-primary fs-1">Lista de Empresas</span>
                <p className="fs-6">Consulte as empresas cadastradas, adicione novas, edite e exclua.</p>

                <div className={loading ? "" : "d-none"}>
                    <LoadingScreen />
                </div>
                <div className={loading ? "d-none" : ""}>
                    <TableEmpresas
                        columns={columns}
                        data={dataEmpresas}
                        titulo="Lista de Empresas"
                    />
                </div>
            </div>

            <Modal size="lg" show={showModalEditarEmpresa} onHide={() => {
                setShowModalEditarEmpresa(false);
                setSelectedUsuario(null);
                setSelectedCursos([]);
            }}>
                <form onSubmit={handleSubmit(onSubmitEditarEmpresa)}>
                    <Modal.Header closeButton>
                        <span className="fw-semibold fs-4 text-primary">Editar empresa</span>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="container">
                            <div className="row">
                                <div className="col-md-12">
                                    <div className="body">
                                        <div className="d-flex">
                                            <div className="d-flex flex-column w-100">
                                                <div className="row mb-3">
                                                    <div className="col-12">
                                                        <div className="mb-3">
                                                            <label className="form-label text-primary fw-medium">Título da Empresa</label>
                                                            <input 
                                                                type="text" 
                                                                {...register("titulo", { required: true })} 
                                                                placeholder="Nome da empresa" 
                                                                className="form-control border-primary" 
                                                            />
                                                            {errors.titulo && <span className='text-danger fs-7'>Título da empresa é obrigatório.</span>}
                                                        </div>
                                                    </div>
                                                </div>
                                                
                                                {/* Campo de seleção de criador - apenas para administradores */}
                                                {nivel === 'Administrador' && (
                                                    <div className="row mb-3">
                                                        <div className="col-12">
                                                            <div className="mb-3">
                                                                <label className="form-label text-primary fw-medium">Criador da Empresa</label>
                                                                <Select
                                                                    value={selectedUsuario}
                                                                    onChange={setSelectedUsuario}
                                                                    options={usuariosOptions}
                                                                    styles={customStyles}
                                                                    placeholder="Selecione o criador da empresa..."
                                                                    isClearable
                                                                    isSearchable
                                                                    noOptionsMessage={() => "Nenhum usuário encontrado"}
                                                                    className="react-select-container"
                                                                    classNamePrefix="react-select"
                                                                />
                                                                <small className="text-muted">
                                                                    Deixe em branco para manter o criador atual
                                                                </small>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Campo de seleção de cursos */}
                                                <div className="row mb-3">
                                                    <div className="col-12">
                                                        <div className="mb-3">
                                                            <label className="form-label text-primary fw-medium">Cursos Permitidos</label>
                                                            <Select
                                                                value={selectedCursos}
                                                                onChange={setSelectedCursos}
                                                                options={cursosOptions}
                                                                placeholder="Selecione os cursos permitidos para esta empresa..."
                                                                isMulti
                                                                styles={customStyles}
                                                                isClearable
                                                                isSearchable
                                                                noOptionsMessage={() => "Nenhum curso encontrado"}
                                                                className="react-select-container"
                                                                classNamePrefix="react-select"
                                                            />
                                                            <small className="text-muted">
                                                                Selecione os cursos que os usuários desta empresa poderão acessar
                                                            </small>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer className="d-flex justify-content-center bg-light">
                        <button
                            type="submit"
                            className="btn btn-primary text-white px-5 py-2"
                            disabled={loadingSave}
                        >
                            {loadingSave ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Salvando...
                                </>
                            ) : (
                                'Salvar'
                            )}
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-primary text-primary px-5 py-2 ms-4"
                            onClick={() => {
                                setShowModalEditarEmpresa(false);
                                setSelectedUsuario(null);
                                setSelectedCursos([]);
                            }}
                            disabled={loadingSave}
                        >
                            Cancelar
                        </button>
                    </Modal.Footer>
                </form>
            </Modal>

            <Modal size="lg" show={showModalDeletarEmpresa.show} onHide={() => setShowModalDeletarEmpresa({ show: false, id: null, nome: '' })}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirmação</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Você tem certeza que deseja deletar a empresa <strong>"{showModalDeletarEmpresa.nome}"</strong>?
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-center bg-light">
                    <button 
                        onClick={() => handleDeletarEmpresa(showModalDeletarEmpresa.id)}
                        className="btn btn-primary text-white px-5 py-2"
                    >
                        Deletar
                    </button>
                    <button 
                        className="btn btn-outline-primary text-primary px-5 py-2 ms-4" 
                        onClick={() => setShowModalDeletarEmpresa({ show: false, id: null, nome: '' })}
                    >
                        Cancelar
                    </button>
                </Modal.Footer>
            </Modal>
        </>
    )
}

export default Empresas; 